import React from "react";

interface ModeRendererProps {
  brand: string;
  today: string;
  Copy: string;
}

export function VasarelyMichelinMode({ brand }: ModeRendererProps) {
  const width = 794;
  const height = 1123;

  // Michelin blue gradient
  const bgGradient = (
    <defs>
      <linearGradient id="michelinGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style={{ stopColor: "#003D7A", stopOpacity: 1 }} />
        <stop offset="50%" style={{ stopColor: "#0066B3", stopOpacity: 1 }} />
        <stop offset="100%" style={{ stopColor: "#1A1A1A", stopOpacity: 1 }} />
      </linearGradient>
    </defs>
  );

  // Radial tire patterns - concentric circles with tread texture
  const tirePatterns: JSX.Element[] = [];
  for (let ring = 0; ring < 20; ring++) {
    const radius = 40 + ring * 20;
    const treadCount = 12 + ring * 2;
    
    // Main circle
    tirePatterns.push(
      <circle
        key={`tire-${ring}`}
        cx={0}
        cy={0}
        r={radius}
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={ring % 2 === 0 ? 2.5 : 1.5}
        opacity={0.4 - ring * 0.012}
      />
    );
    
    // Tread texture marks
    for (let t = 0; t < treadCount; t++) {
      const angle = (t / treadCount) * 2 * Math.PI;
      const x1 = Math.cos(angle) * radius;
      const y1 = Math.sin(angle) * radius;
      const x2 = Math.cos(angle) * (radius + 8);
      const y2 = Math.sin(angle) * (radius + 8);
      
      tirePatterns.push(
        <line
          key={`tread-${ring}-${t}`}
          x1={x1}
          y1={y1}
          x2={x2}
          y2={y2}
          stroke="#0066B3"
          strokeWidth={1}
          opacity={0.3}
        />
      );
    }
  }

  // Motion arcs - kinetic rotation curves
  const motionArcs: JSX.Element[] = [];
  for (let arc = 0; arc < 10; arc++) {
    const startAngle = (arc / 10) * 2 * Math.PI;
    const endAngle = startAngle + Math.PI * 0.4;
    const radius = 150 + arc * 35;
    
    const x1 = Math.cos(startAngle) * radius;
    const y1 = Math.sin(startAngle) * radius;
    const x2 = Math.cos(endAngle) * radius;
    const y2 = Math.sin(endAngle) * radius;
    
    // Quadratic bezier for smooth arc
    const controlAngle = (startAngle + endAngle) / 2;
    const controlR = radius * 1.15;
    const cx = Math.cos(controlAngle) * controlR;
    const cy = Math.sin(controlAngle) * controlR;
    
    motionArcs.push(
      <path
        key={`arc-${arc}`}
        d={`M ${x1},${y1} Q ${cx},${cy} ${x2},${y2}`}
        fill="none"
        stroke="#FFFFFF"
        strokeWidth={2}
        opacity={0.25}
      />
    );
  }

  // Resilience feedback loops - spiral patterns
  const feedbackLoops: JSX.Element[] = [];
  for (let spiral = 0; spiral < 4; spiral++) {
    const points: string[] = [];
    const spiralOffset = (spiral / 4) * 2 * Math.PI;
    
    for (let p = 0; p <= 60; p++) {
      const t = p / 60;
      const angle = t * Math.PI * 6 + spiralOffset;
      const r = 80 + t * 280;
      const x = Math.cos(angle) * r;
      const y = Math.sin(angle) * r;
      points.push(`${x},${y}`);
    }
    
    feedbackLoops.push(
      <polyline
        key={`spiral-${spiral}`}
        points={points.join(" ")}
        fill="none"
        stroke="#0066B3"
        strokeWidth={1.5}
        opacity={0.2}
      />
    );
  }

  // Circular trust nodes - reliability waypoints
  const trustNodes: JSX.Element[] = [];
  const nodeAngles = [0, Math.PI / 3, 2 * Math.PI / 3, Math.PI, 4 * Math.PI / 3, 5 * Math.PI / 3];
  const nodeRadius = 260;
  
  nodeAngles.forEach((angle, i) => {
    const nx = Math.cos(angle) * nodeRadius;
    const ny = Math.sin(angle) * nodeRadius;
    
    trustNodes.push(
      <g key={`trust-${i}`}>
        <circle
          cx={nx}
          cy={ny}
          r={40}
          fill="none"
          stroke="#FFFFFF"
          strokeWidth={3}
          opacity={0.5}
        />
        <circle
          cx={nx}
          cy={ny}
          r={28}
          fill="none"
          stroke="#0066B3"
          strokeWidth={2}
          opacity={0.4}
        />
        <circle
          cx={nx}
          cy={ny}
          r={16}
          fill="#0066B3"
          opacity={0.3}
        />
      </g>
    );
  });

  // Radial velocity lines
  const velocityLines: JSX.Element[] = [];
  for (let v = 0; v < 36; v++) {
    const angle = (v / 36) * 2 * Math.PI;
    const x = Math.cos(angle) * 380;
    const y = Math.sin(angle) * 380;
    
    velocityLines.push(
      <line
        key={`velocity-${v}`}
        x1={0}
        y1={0}
        x2={x}
        y2={y}
        stroke="#FFFFFF"
        strokeWidth={v % 6 === 0 ? 2 : 1}
        opacity={v % 6 === 0 ? 0.25 : 0.1}
        strokeDasharray="8,4"
      />
    );
  }

  // Central Michelin man abstraction - circular bibendum
  const bibendum = (
    <g opacity={0.2}>
      <circle cx={0} cy={-25} r={22} fill="none" stroke="#FFFFFF" strokeWidth={3} />
      <circle cx={0} cy={0} r={28} fill="none" stroke="#FFFFFF" strokeWidth={3} />
      <circle cx={0} cy={28} r={24} fill="none" stroke="#FFFFFF" strokeWidth={3} />
    </g>
  );

  return (
    <svg width={width} height={height} xmlns="http://www.w3.org/2000/svg">
      {bgGradient}
      <rect width={width} height={height} fill="url(#michelinGradient)" />
      <g transform={`translate(${width / 2}, ${height / 2})`}>
        {velocityLines}
        {feedbackLoops}
        {tirePatterns}
        {motionArcs}
        {trustNodes}
        {bibendum}
      </g>
    </svg>
  );
};
